import { CartFlagsService, Guid, SessionStorageKeys } from '@aglenergy/shared-library';
import { Component, ComponentInterface, JSX, Prop, State, h } from '@stencil/core';
import {
  EligibilityValidation,
  EligibilityValidationSource,
  EligibilityValidationSourceWithPromoCodeDls
} from 'app/shared/constants/common.model';
import {
  ProductCatalogueContentFeature,
  ProductCatalogueProduct,
  SelectedProductDetails,
  SelectedProductSessionData
} from 'app/shared/models/index';
import { getWithoutOfferStrip, renderContentBelowCards } from 'app/shared/utilities/helper-functions';

import { CommonLinks } from 'app/shared/components/common-links/common-links';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { bundleService } from 'app/shared/services/bundle/bundle.service';
import { couponCodeService } from 'app/shared/services/coupon-code/coupon-code.service';
import { crossSellService } from 'app/shared/services/cross-sell/cross-sell.service';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { productService } from 'app/shared/services/productService/product.service';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';
import { store } from 'app/shared/store/store';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';

@Component({
  tag: 'mobile-product-card',
  styleUrl: 'mobile-product-card.scss',
  scoped: true
})
export class MobileProductCard implements ComponentInterface {
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public isPersonalisedProductsLoading: boolean;
  @Prop() public focusCardIndex: number;
  @State() public selectedProductSessionData: SelectedProductSessionData = JSON.parse(
    window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails)
  );
  @State() isGuidedFlow = false;

  private cardIdPrefix = 'card';
  private submitButtonIdPrefix = 'mobilePlanCardSubmitButton';
  private selectedMobileProductListener: () => void;

  public componentWillLoad(): Promise<void> | void {
    this.selectedMobileProductListener = store.onChange(StoreKeys.selectedMobileProductDetails, (selectedMobileProductDetails) => {
      if (selectedMobileProductDetails) {
        this.selectedProductSessionData = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
      }
    });
    this.isGuidedFlow = store.get('isGuidedFlow');

    store.on('dispose', () => {
      this.selectedMobileProductListener();
    });
  }

  public disconnectedCallback(): void {
    this.selectedMobileProductListener();
  }

  public addSelectedProduct(sku: string): void {
    let selectedProduct = this.products.find((x) => x.sku === sku);
    selectedProduct = { ...selectedProduct, cartItemId: Guid.newGuid() };
    this.selectedProductSessionData = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
    const totalProductCount: number = this.selectedProductSessionData?.productDetails?.length
      ? this.selectedProductSessionData.productDetails.length
      : 0;

    const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
    customerProfileService.setCustomerProfile(customerProfile);
    const couponCode = store.get('couponCode');
    if (couponCode) {
      couponCodeService.setSessionStorage(couponCode);
    } else if (store.get('couponCodes').find((c) => c.productCategory === 'mobile plan')) {
      couponCodeService.setMultipleCouponSessionStorage(store.get('couponCodes')?.find((c) => c.productCategory === 'mobile plan'));
    } else if (!this.isGuidedFlow) {
      couponCodeService.resetSessionStorage();
    }
    switch (true) {
      case totalProductCount === 0:
        this.selectedProductSessionData = {
          productDetails: [
            {
              product: selectedProduct
            }
          ],
          customerProfile
        };
        this.setProductAsCartItem(selectedProduct);
        tealiumAnalyticService.trackPlanSelectionData(selectedProduct, 'mobile plan', 'Add Plan');
        break;
      case totalProductCount < 6:
        this.selectedProductSessionData.productDetails.push({ product: selectedProduct });
        this.setProductAsCartItem(selectedProduct);
        tealiumAnalyticService.trackPlanSelectionData(selectedProduct, 'mobile plan', 'Add', totalProductCount);
        break;
      default: {
        const earliestAddedProduct = this.selectedProductSessionData.productDetails[0].product;
        this.removeSelectedProduct(earliestAddedProduct);
        this.selectedProductSessionData.productDetails.push({ product: selectedProduct });
        this.setProductAsCartItem(selectedProduct);
        tealiumAnalyticService.trackPlanSelectionData(selectedProduct, 'mobile plan', 'Add', totalProductCount);
        break;
      }
    }

    if (crossSellService.isCrossSellFlow()) {
      customerProfile.isBundleExplicitlyRequestedByCustomer = false;
      customerProfileService.setCustomerProfile(customerProfile);
    }
  }

  public removeSelectedProduct(product: ProductCatalogueProduct): void {
    this.selectedProductSessionData = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
    for (let i = 0; i < this.selectedProductSessionData.productDetails.length; ++i) {
      if (this.selectedProductSessionData.productDetails[i].product.sku === product.sku) {
        store.set(StoreKeys.removeProductFromCartItem, this.selectedProductSessionData.productDetails[i].product);
        this.selectedProductSessionData.productDetails.splice(i, 1);
        break;
      }
    }
    productsSessionStorageService.saveMobileProducts(this.selectedProductSessionData);

    tealiumAnalyticService.trackPlanSelectionData(
      product,
      'mobile plan',
      'Remove',
      this.selectedProductSessionData?.productDetails?.length
    );
  }

  public getProductAltCost(price: string, priceVariable: string): string {
    return `Or $${parseInt(price, 10)}${priceVariable} without AGL energy.`;
  }

  private setProductAsCartItem(selectedProductDetails: ProductCatalogueProduct): void {
    if (selectedProductDetails && this.isGuidedFlow) {
      const cartItem = bundleService.mapMobileProductCatalogueAsCartItem(selectedProductDetails);
      store.set(StoreKeys.addProductToCartItem, [cartItem]);
      productsSessionStorageService.saveMobileProducts(this.selectedProductSessionData);
    }
  }

  private getProductCount(selectedProduct: ProductCatalogueProduct): number {
    let productCount = 0;
    this.selectedProductSessionData?.productDetails?.forEach((product: SelectedProductDetails) =>
      product.product.sku == selectedProduct.sku ? ++productCount : null
    );
    return productCount;
  }

  private choosePlanButton(product: ProductCatalogueProduct, planCardIndex: number): JSX.Element {
    const selectedProductCount = this.getProductCount(product);
    if (this.isGuidedFlow && selectedProductCount == 0) {
      return (
        <dls-button
          variant="primary"
          loading={this.isPersonalisedProductsLoading || store.get('skuBeingRemoved') === 'clearCart'}
          data-testid="sim-zero"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => !this.isPersonalisedProductsLoading && this.addSelectedProduct(product.sku)}
        >
          Choose this plan
        </dls-button>
      );
    }
    if (this.isGuidedFlow && selectedProductCount > 0) {
      return (
        <div class="iterative-button__container">
          <button
            class="iterative-button__button"
            data-testid="sim-remove"
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => !this.isPersonalisedProductsLoading && this.removeSelectedProduct(product)}
          >
            <svg class="iterative-button" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle class="iterative-button-circle" cx="16" cy="16" r="16" fill="#E6E8F7" />
              <path
                class="iterative-button-path"
                d="M17 15H22C22.6 15 23 15.4 23 16C23 16.6 22.6 17 22 17H17H15H10C9.4 17 9 16.6 9 16C9 15.4 9.4 15 10 15H15H17Z"
                fill="#001CB0"
              />
            </svg>
          </button>
          <dls-text class="iterative-button__label" appearance="highlight" fontWeight="regular" font-size="lg">
            {selectedProductCount} SIM plan
          </dls-text>
          <button
            class="iterative-button__button"
            data-testid="sim-add"
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => !this.isPersonalisedProductsLoading && this.addSelectedProduct(product.sku)}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle class="iterative-button-circle" cx="16" cy="16" r="16" fill="#E6E8F7" />
              <path
                class="iterative-button-path"
                d="M22 15H17V10C17 9.4 16.6 9 16 9C15.4 9 15 9.4 15 10V15H10C9.4 15 9 15.4 9 16C9 16.6 9.4 17 10 17H15V22C15 22.6 15.4 23 16 23C16.6 23 17 22.6 17 22V17H22C22.6 17 23 16.6 23 16C23 15.4 22.6 15 22 15Z"
                fill="#001CB0"
              />
            </svg>
          </button>
        </div>
      );
    }
    if (!this.isGuidedFlow) {
      return (
        <dls-button
          id={`${this.submitButtonIdPrefix}${planCardIndex}`}
          variant="primary"
          loading={this.isPersonalisedProductsLoading}
          data-testid="sim-default"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => !this.isPersonalisedProductsLoading && this.onSelectedProduct(product.sku, planCardIndex)}
        >
          Choose this plan
        </dls-button>
      );
    }
  }

  public onSelectedProduct(sku: string, planCardIndex: number): void {
    store.set(StoreKeys.selectedSKU, sku);
    const productDetailsFromSession = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    const differentiatedPricing =
      productDetailsFromSession?.length > 0 ? productDetailsFromSession[0]?.response?.products[0]?.differentiatedPricing : null;
    const eligibilityValidation = differentiatedPricing?.eligibilityValidation;
    const eligibilityValidationSource = differentiatedPricing?.eligibilityValidationSource;

    if (
      eligibilityValidation === EligibilityValidation.SignupValidation &&
      Object.entries(EligibilityValidationSource)?.some(
        ([, value]) => value.toLowerCase() === (eligibilityValidationSource as string).toLowerCase()
      )
    ) {
      // This if for promo-dialog to open.
      const userPromoModalDls = Object.entries(EligibilityValidationSourceWithPromoCodeDls)?.some(
        ([, value]) => value.toLowerCase() === (eligibilityValidationSource as string).toLowerCase()
      );

      const isPromoModalKey = userPromoModalDls ? StoreKeys.isPromoModalDlsOpen : StoreKeys.isPromoModalOpen;
      store.set(isPromoModalKey, true);
      store.set(StoreKeys.promoModalTriggerElementId, `${this.submitButtonIdPrefix}${planCardIndex}`);
    } else {
      productService.onSelectedProductForMobile(sku, this.products, this.isGuidedFlow);
    }
  }

  private getFeatureIcon(featureIconPath?: string): JSX.Element {
    if (!featureIconPath) {
      return null;
    }
    return <dls-dynamic-icon iconPath={featureIconPath} altText="" />;
  }

  public getInCartSku(): string {
    const cartDetails = store.get(StoreKeys.cart);
    if (!cartDetails) {
      return null;
    }
    const cartService = new CartFlagsService(cartDetails);
    const inCartNbnProduct = cartService.getNbnProducts()?.[0];
    return inCartNbnProduct ? inCartNbnProduct.sku : null;
  }

  public render(): JSX.Element {
    const inCartSku = this.getInCartSku();

    const anyStripText = this.products?.some((product) => product.content.stripText);

    document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
    const productCategory = store.get(StoreKeys.productCategory);
    const generalContentCategoryItems = store.get(StoreKeys.generalContent).categoryItems;

    return (
      <div>
        <div class="comparison-container" id="comparison-container">
          {this.products && (
            <carousel-buttons numberOfProducts={this.products.length} focusedCardIndex={this.focusCardIndex}>
              <div class="card-container" id="card-container">
                {this.products.map((product, index) => {
                  const isProductInCart = product.sku === inCartSku;
                  return this.mobileCardRender(isProductInCart, anyStripText, product, index);
                })}
              </div>
            </carousel-buttons>
          )}
        </div>
        {generalContentCategoryItems !== undefined && generalContentCategoryItems.length && (
          <CommonLinks
            generalContentCategoryItems={generalContentCategoryItems}
            productCategory={productCategory}
            selectedPlanCategory={''}
            techType={''}
          />
        )}
        <div class="below-cards-message">{renderContentBelowCards(this.products)}</div>
      </div>
    );
  }

  private mobileCardRender(isProductInCart: boolean, anyStripText: boolean, product: ProductCatalogueProduct, index: number): any {
    const displayToUser = product.qualified !== false;
    const productHeadline = product.content.productHeadline.replace(/nbn/gi, '<strong>nbn</strong>');
    const displayWithoutOfferStrip = getWithoutOfferStrip(product);

    return (
      <div class="card__wrapper">
        <div
          class={`card scroll-snap ${isProductInCart ? 'card__plan-selected' : ''} ${!displayToUser ? 'unavailable' : ''}`}
          id={`${this.cardIdPrefix}${index}`}
        >
          <dls-card
            padding="md"
            elevationType="level1"
            radius="round02"
            stripColor={product.content.stripText ? 'secondary-04' : 'none'}
            stripText={product.content.stripText}
            topMargin={anyStripText && !product.content.stripText ? 'space04' : 'none'}
            role="tabpanel"
          >
            <div>
              <dls-h6>{product.content.productName}</dls-h6>
              <dls-h3>{productHeadline}</dls-h3>
            </div>
            <div class="unavailable-overlay-container">
              <div class={!displayToUser && 'unavailable-overlay'} hidden={displayToUser}>
                <info-tag heading={product.content.unavailableHeading} content={product.content.unavailableContent} />
              </div>
              <div>
                <telco-new-price-tag
                  price={product.content.bundleOfferPrice || product.attributes.agl_marketing_price}
                  paymentFreq={product.content.priceVariable}
                  priceDesc={displayToUser ? product.content.bundleOfferText : 'Bundle Price ...'}
                  bundleTitle={product.content.bundleOfferStrip}
                />

                {displayWithoutOfferStrip && (
                  <telco-new-price-tag
                    class="without-offer-price-tag"
                    price={product.content.withoutOfferPrice || product.price}
                    paymentFreq={product.content.priceVariable}
                    priceDesc={product.content.withoutOfferText}
                  />
                )}
              </div>
              <div class="feature-list">
                {product.content?.features.map((feature: ProductCatalogueContentFeature) => {
                  return (
                    <telco-feature-text desc={feature.featureDescription}>{this.getFeatureIcon(feature.featureIcon)}</telco-feature-text>
                  );
                })}
              </div>
              <div class="cta--button">{this.choosePlanButton(product, index)}</div>
              <div class="links">
                {product.content.links?.map((link) => {
                  return (
                    <div>
                      <dls-link href={link.url} open-in-new-window>
                        {link.title}
                      </dls-link>
                    </div>
                  );
                })}
              </div>
            </div>
          </dls-card>
        </div>
      </div>
    );
  }
}
